import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Table, ColumnInformation, TableSorterInformation, TableSorterField, TableSorter, SelectInput} from "@utahdts/dts-react-common";
import {contentLength, formatAmount, MAX_CHARACTERS, MEDIA_SIZES} from "../misc/Shared";
import {iconArrowRight} from "../common/Icon";
import webservice from '../common/Webservice';
import Media from "react-media";
import {Link} from 'react-router-dom';

const propTypes = {
	currentCobiId:  PropTypes.number,

	currentFund: PropTypes.string,

	currentYear:  PropTypes.number,

	draft: PropTypes.bool,

	history: PropTypes.object.isRequired
};

const defaultProps = {
};

// Props connected to the store
const mapStateToProps = (state) => {
	return {
		currentFund: state.app.currentFund,
		currentCobiId: state.app.currentCobiId,
		currentYear: state.app.currentYear,
		draft: state.app.draft
	};
};

class Fund extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			fund: undefined,
			legalName: undefined,
			legalAuth: undefined,
			revenueSource: undefined,
			description: undefined,
			history: undefined,
			searchFundOptions: [],
			searchFundInput: undefined,
			mobileYearsOptions: [],
			mobileYears: [],
			readMore: false
		};

		if(this.props.currentFund !== undefined) {
			webservice.public.fund.get(this.props.currentFund).then(data => {
				const sortedHistory = data.history.sort((a, b) => a.year === b.year ? 0 : a.year === null ? -1 : b.year === null ? 1 : b.year - a.year);
				this.setState({
					fund: data.fund,
					legalName: data.legalName,
					legalAuth: data.legalAuth,
					revenueSource: data.revenueSource,
					description: data.description,
					history: sortedHistory,
					mobileYearsOptions: sortedHistory.map(year => ({label: year.year.toString(), value: year.year})),
					mobileYears: sortedHistory.slice(0, 2).map(year => (year.year)),
					earnsInterest: data.earnsInterest,
					earnsInterestAuth: data.earnsInterestAuth,
				})
			});
		}

		webservice.public.fund.fundsList().then(list => this.setState({ searchFundOptions: list }));

		const sortYear = new TableSorterField('year', TableSorter.SORT.NUMBER, TableSorter.DIRECTION.ASCENDING);
		const sortBeginBalance = new TableSorterField('beginBalance', TableSorter.SORT.NUMBER, TableSorter.DIRECTION.ASCENDING);
		const sortRevenues = new TableSorterField('revenues', TableSorter.SORT.NUMBER, TableSorter.DIRECTION.ASCENDING);
		const sortExpenses = new TableSorterField('expenses', TableSorter.SORT.NUMBER, TableSorter.DIRECTION.ASCENDING);
		const sortTransfers = new TableSorterField('transfers', TableSorter.SORT.NUMBER, TableSorter.DIRECTION.ASCENDING);
		const sortEndBalance = new TableSorterField('endBalance', TableSorter.SORT.NUMBER, TableSorter.DIRECTION.ASCENDING);

		this.columns = [
			new ColumnInformation({
				title: 'Fiscal Year',
				field: 'year',
				tableSorterInformation: new TableSorterInformation([sortYear]),
			}),
			new ColumnInformation({
				title: 'Beginning Balance',
				field: 'beginBalance',
				tableSorterInformation: new TableSorterInformation([sortBeginBalance]),
				tdCallback: (columnInfo, objectData) => (<td key={columnInfo.field ? columnInfo.field : columnInfo.key}>{formatAmount(objectData.beginBalance, 0)}</td>)
			}),
			new ColumnInformation({
				title: 'Revenues',
				field: 'revenues',
				tableSorterInformation: new TableSorterInformation([sortRevenues]),
				tdCallback: (columnInfo, objectData) => (<td key={columnInfo.field ? columnInfo.field : columnInfo.key}>{formatAmount(objectData.revenues, 0)}</td>)
			}),
			new ColumnInformation({
				title: 'Expenses',
				field: 'expenses',
				tableSorterInformation: new TableSorterInformation([sortExpenses]),
				tdCallback: (columnInfo, objectData) => (<td key={columnInfo.field ? columnInfo.field : columnInfo.key}>{formatAmount(objectData.expenses, 0)}</td>)
			}),
			new ColumnInformation({
				title: 'Transfers',
				field: 'transfers',
				tableSorterInformation: new TableSorterInformation([sortTransfers]),
				tdCallback: (columnInfo, objectData) => (<td key={columnInfo.field ? columnInfo.field : columnInfo.key}>{formatAmount(objectData.transfers, 0)}</td>)
			}),
			new ColumnInformation({
				title: 'Ending Balance',
				field: 'endBalance',
				tableSorterInformation: new TableSorterInformation([sortEndBalance]),
				tdCallback: (columnInfo, objectData) => (<td key={columnInfo.field ? columnInfo.field : columnInfo.key}>{formatAmount(objectData.endBalance, 0)}</td>)
			}),
		];
	}

	componentDidMount() {
		window.scroll({top: 0, left: 0});
	}

	componentDidUpdate(prevProps) {
		if(prevProps.currentFund !== this.props.currentFund && this.props.currentFund) {
			webservice.public.fund.get(this.props.currentFund).then(data => {
				const sortedHistory = data.history.sort((a, b) => a.year === b.year ? 0 : a.year === null ? -1 : b.year === null ? 1 : b.year - a.year);
				this.setState({
					fund: data.fund,
					legalName: data.legalName,
					legalAuth: data.legalAuth,
					revenueSource: data.revenueSource,
					description: data.description,
					history: sortedHistory,
					earnsInterest: data.earnsInterest,
					earnsInterestAuth: data.earnsInterestAuth,
					readMore: false,
					mobileYears: sortedHistory.slice(0, 2).map(year => (year.year)),
					mobileYearsOptions: sortedHistory.map(year => ({label: year.year.toString(), value: year.year})),
				})
			});
		}
	}

	renderDescriptionAndGrayBox() {
		return (
			<section className="description-area clearfix">
				<div className="gray-box atid_legal-auth">
					<strong>Legal Authorization</strong>
					<p>{this.state.legalAuth}</p>

					<strong>Revenue Sources</strong>
					<p>{this.state.revenueSource}</p>

					<p className="italic-text">{this.state.earnsInterest && `This fund earns interest (${this.state.earnsInterestAuth}).`}</p>
				</div>
				<div>
					<h3>Description and History</h3>
					<p className="white-space pre-line">{this.state.description}</p>
				</div>
			</section>
		);
	}

	readMore(event, show) {
        this.setState({readMore: show});

        !show && window.scroll({top: event.target.offsetTop, left: 0});
	}

	render() {
		return (
			<React.Fragment>
				<SelectInput
					field="searchFundInput"
					label=""
					placeholder="Search for a fund..."
					className="Select--search content atid_fund-search"
					showLabel={false}
					allowClear={true}
					showSearch={true}
					options={this.state.searchFundOptions}
					value={this.state.searchFundInput}
					onChange={(field, val) => this.setState({ searchFundInput: val }, () => val && this.props.history.push(`/fund/${val}`))}
				/>
				<Link to={`/${this.props.currentYear}/${this.props.currentCobiId}/financials${this.props.draft ? '/draft' : ''}`} className="italic-text content atid_back-to-financials">
					<span className="reverse">{iconArrowRight()}</span> Back to financials
				</Link>
				<h1>{this.state.legalName}</h1>
				<div className="content">
					<Media query={MEDIA_SIZES.TABLET_NARROW_MAX_WIDTH}>
						{matches =>
							matches ? (
								<React.Fragment>
									<div className={contentLength(this.state.description, MAX_CHARACTERS) ? this.state.readMore ? "mb-spacing" : "read-more" : "mb-spacing"}>{this.renderDescriptionAndGrayBox()}</div>
									{contentLength(this.state.description, MAX_CHARACTERS) && (!this.state.readMore ?
									<a onClick={(e) => this.readMore(e, true)} className="mb-spacing bold-text inline-block atid_read-more-link">
										READ MORE <span className="ml-spacing-small">&raquo;</span>
									</a>
									:
                                    <a onClick={(e) => this.readMore(e, false)}  className="mb-spacing bold-text inline-block atid_read-more-link">
                                        READ LESS <span className="ml-spacing-small">&raquo;</span>
									</a>
									)}
								</React.Fragment>
							) : (
								this.renderDescriptionAndGrayBox()
							)
						}
					</Media>
					<section>
						<h3>Fund History</h3>
						{this.state.history && <Media query={MEDIA_SIZES.TABLET_NARROW_MAX_WIDTH}>
							{matches =>
								matches ? (
										<div>
											<SelectInput
												field="mobileYears"
												label="Fiscal Years:"
												allowClear={false}
												showSearch={false}
												className="mr-spacing stack-form full-mobile atid_years-select"
												options={this.state.mobileYearsOptions}
												value={this.state.mobileYears}
												onChange={(field, val) => this.setState({mobileYears: val})}
												multiSelect={true}
											/>

											{this.state.history.map((data, indexH) =>
												(this.state.mobileYears.includes(data.year) && <div key={data.title + indexH.toString()} className={`row atid_table-row-mobile${indexH}`}>
													{this.columns.map((column, indexC) =>
														(<div key={column.field} className={indexC > 0 ? "flex sub-row" : "sub-row"}>
															{indexC > 0 && <div className="title">{column.title}:</div>}
															{column.title !== 'Fiscal Year'
																? <div className="data">{formatAmount(data[column.field], 0)}</div>
																: <div className="title bold-text">{data[column.field]}</div>
															}
														</div>)
													)}
												</div>)
											)}
										</div>
									) : (
										<Table
											columns={this.columns}
											list={this.state.history}
											className="mb-spacing"
											defaultSortField={'year'}
										/>
									)
							}
						</Media> }
						<p className="atid_footer-disclaimer note">This report presents unofficial, unaudited information that is subject to change. <br/>
							For audited financial statements, see the State of Utah Comprehensive Annual Financial Report at <a href="https://le.utah.gov/CobiServlet/cobi/BudgetRD?key=cafr" target="_blank">https://le.utah.gov/CobiServlet/cobi/BudgetRD?key=cafr</a>.</p>
					</section>
				</div>
			</React.Fragment>
		);
	}
}

Fund.propTypes = propTypes;
Fund.defaultProps = defaultProps;

export default connect(mapStateToProps)(Fund);
